/**
 * Quote Action Service
 * Handles quote status actions: mark accepted, rejected, sent
 */

import api from "../../../shared/api";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Mark quote as accepted with payment confirmation and create order
 * @param {string} quoteId - Quote ID
 * @param {Object} paymentData - Payment data
 * @param {string} paymentData.paymentReference - Payment reference (required)
 * @param {string} paymentData.confirmationType - Payment confirmation type: "proof_received" | "money_cleared"
 * @param {string} [paymentData.notes] - Additional notes
 * @param {string} [paymentData.adminId] - Admin ID who performed the action
 * @returns {Promise<Object>} Result with order data
 */
export const markQuoteAcceptedWithPayment = async (quoteId, paymentData) => {
  try {
    const response = await api.post(
      `/quotes/update/${quoteId}/accept-with-payment`,
      {
        paymentReference: paymentData.paymentReference,
        confirmationType: paymentData.confirmationType,
        notes: paymentData.notes || "",
        adminId: paymentData.adminId,
      }
    );

    if (!response.success) {
      throw new Error(
        response.data.error || "Failed to mark quote as accepted"
      );
    }

    return {
      success: true,
      data: response.data.data,
      message:
        response.data.message ||
        "Quote accepted and order created successfully",
    };
  } catch (error) {
    logError("Error marking quote as accepted", {
      quoteId,
      paymentData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to mark quote as accepted",
    };
  }
};

/**
 * Mark quote as rejected
 * @param {string} quoteId - Quote ID
 * @param {Object} rejectionData - Rejection data
 * @param {string} [rejectionData.reason] - Reason for rejection
 * @param {string} [rejectionData.adminId] - Admin ID who performed the action
 * @returns {Promise<Object>} Result
 */
export const markQuoteRejected = async (quoteId, rejectionData = {}) => {
  try {
    const response = await api.patch(`/quotes/update/${quoteId}/status`, {
      status: "rejected",
      notes: rejectionData.reason || "",
    });

    if (!response.success) {
      throw new Error(
        response.data.error || "Failed to mark quote as rejected"
      );
    }

    return {
      success: true,
      data: response.data.data,
      message: response.data.message || "Quote marked as rejected successfully",
    };
  } catch (error) {
    logError("Error marking quote as rejected", {
      quoteId,
      rejectionData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to mark quote as rejected",
    };
  }
};

/**
 * Mark quote as sent
 * @param {string} quoteId - Quote ID
 * @param {string} [adminId] - Admin ID who performed the action
 * @returns {Promise<Object>} Result
 */
export const markQuoteAsSent = async (quoteId, adminId = null) => {
  try {
    // Use the generic status update endpoint which is more reliable
    const response = await api.patch(`/quotes/update/${quoteId}/status`, {
      status: "sent",
      notes: "Marked as sent by admin",
    });

    if (!response.success) {
      throw new Error(response.error || "Failed to mark quote as sent");
    }

    return {
      success: true,
      data: response.data,
      message: response.message || "Quote marked as sent successfully",
    };
  } catch (error) {
    logError("Error marking quote as sent", {
      quoteId,
      adminId,
      error: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to mark quote as sent",
    };
  }
};
